import { Component, Input, Output, EventEmitter, forwardRef, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';

@Component({
    selector: 'app-search-bar',
    imports: [
        CommonModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputTextModule
    ],
    standalone: true,
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchBarComponent),
            multi: true
        }
    ]
})
export class SearchBarComponent implements ControlValueAccessor, OnDestroy {
    @Input() placeholder: string = 'Search...';
    @Input() width: string = '100%';
    @Input() disabled: boolean = false;
    @Input() value: string = '';

    /**
     * Debounce delay in milliseconds before `searchChange` / `search` fire.
     * Defaults to 300 ms. Set to 0 to emit immediately (no debounce).
     */
    @Input() debounceMs: number = 300;

    @Output() searchChange = new EventEmitter<string>();
    @Output() search = new EventEmitter<string>();

    // ControlValueAccessor callbacks
    private onChange = (_value: string) => {};
    private onTouched = () => {};

    /** Timer handle for the active debounce window. */
    private _debounceTimer: ReturnType<typeof setTimeout> | null = null;

    ngOnDestroy(): void {
        // Clear any pending debounce on destroy to prevent memory leaks
        this._clearDebounce();
    }

    writeValue(value: string): void {
        this.value = value || '';
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onInputChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;

        // Update internal value and notify reactive forms immediately
        this.value = value;
        this.onChange(value);
        this.onTouched();

        // Debounce the output emissions so rapid keystrokes don't flood listeners
        this._clearDebounce();

        if (this.debounceMs > 0) {
            this._debounceTimer = setTimeout(() => this._emit(value), this.debounceMs);
        } else {
            // Zero delay — emit synchronously (backwards-compatible behaviour)
            this._emit(value);
        }
    }

    private _emit(value: string): void {
        this.searchChange.emit(value);
        this.search.emit(value);
    }

    private _clearDebounce(): void {
        if (this._debounceTimer !== null) {
            clearTimeout(this._debounceTimer);
            this._debounceTimer = null;
        }
    }
}
